export const slides = [
  {
    id: 1,
    type: 'title',
    title: 'vExpertAI GmbH',
    subtitle: 'AI-Powered Network Operations & Security',
    tagline: 'The Network and Security Architect You\'ve Always Wanted—Powered by AI',
    badge: 'AWS Credits Application'
  },
  {
    id: 2,
    type: 'problem',
    title: 'The Problem',
    subtitle: 'Critical Challenges in Network Operations',
    points: [
      {
        icon: 'UserX',
        title: 'Expert Shortage',
        description: 'Only 65,000 CCIEs worldwide for millions of enterprise networks. Finding and retaining experts is nearly impossible.'
      },
      {
        icon: 'Clock',
        title: 'Slow Resolution',
        description: 'Average MTTR of 4-8 hours for network incidents. Every minute of downtime costs enterprises $5,600 on average.'
      },
      {
        icon: 'Shield',
        title: 'Security Gaps',
        description: '60% of breaches involve network vulnerabilities. Manual security audits can\'t keep pace with evolving threats.'
      },
      {
        icon: 'TrendingDown',
        title: 'Reactive Operations',
        description: '73% of network teams spend time firefighting instead of innovation. Predictive capabilities remain elusive.'
      }
    ]
  },
  {
    id: 3,
    type: 'solution',
    title: 'Our Solution',
    subtitle: 'Autonomous Network Operations AI Platform',
    description: 'Industry-first AI agents that analyze, diagnose, and resolve network issues autonomously',
    features: [
      { icon: 'Brain', label: '5 Specialized AI Agents', desc: 'Team Leader orchestrates specialists' },
      { icon: 'Zap', label: '8-13 Second Response', desc: 'From alert to root cause analysis' },
      { icon: 'Lock', label: 'Protocol-Compliant', desc: 'ACP, A2A, and MCP standards' },
      { icon: 'Server', label: 'Real Device Integration', desc: 'Production-tested on Cisco routers' }
    ]
  },
  {
    id: 4,
    type: 'product',
    title: 'Product Suite',
    subtitle: '5 Ready-to-Deploy AI Solutions',
    products: [
      { icon: 'ShieldCheck', name: 'Security Validation', desc: 'Real-time CVE analysis & compliance' },
      { icon: 'Copy', name: 'Digital Twin Simulation', desc: 'Test changes safely before deployment' },
      { icon: 'GraduationCap', name: 'Virtual Expert/Architect', desc: '24/7 AI-powered network expertise' },
      { icon: 'Monitor', name: 'Virtual NOC', desc: 'Autonomous operations center' },
      { icon: 'MessageSquare', name: 'Conversational Assistant', desc: 'Natural language management' }
    ]
  },
  {
    id: 5,
    type: 'technology',
    title: 'Technology Stack',
    subtitle: 'Enterprise-Grade AI Infrastructure',
    stack: [
      { category: 'AI/ML', items: ['Fine-tuned LLMs', 'RAG Technology', 'Multi-Agent Architecture'] },
      { category: 'Protocols', items: ['ACP (Agent Communication)', 'A2A (Agent-to-Agent)', 'MCP (Model Context)'] },
      { category: 'Network', items: ['Multi-Vendor Support', 'Cisco, Juniper, Arista', 'Real-time Telemetry'] },
      { category: 'Security', items: ['SOC2 Compliant', 'End-to-End Encryption', 'Zero Trust Architecture'] }
    ]
  },
  {
    id: 6,
    type: 'rnd',
    title: 'R&D Roadmap',
    subtitle: 'Building the Future of Network AI',
    mainProject: {
      title: 'Proprietary LLM Development',
      description: 'Developing a specialized Large Language Model trained exclusively on network operations, security protocols, and infrastructure management data.',
      phases: [
        { phase: 'Phase 1', title: 'Data Collection', status: 'completed', desc: 'Network configs, logs, troubleshooting data' },
        { phase: 'Phase 2', title: 'Base Model Training', status: 'in-progress', desc: 'Training on curated network corpus' },
        { phase: 'Phase 3', title: 'Fine-tuning & RLHF', status: 'planned', desc: 'Expert feedback integration' },
        { phase: 'Phase 4', title: 'Production Deployment', status: 'planned', desc: 'Edge-optimized inference' }
      ]
    },
    capabilities: [
      { icon: 'Sparkles', title: 'LLM Fine-tuning', desc: 'Custom fine-tuning of GPT-4, Claude, Llama for network-specific tasks' },
      { icon: 'Brain', title: 'LRM Development', desc: 'Large Reasoning Models for complex network diagnostics' },
      { icon: 'Cpu', title: 'Model Optimization', desc: 'Quantization & pruning for edge deployment' },
      { icon: 'Database', title: 'Training Infrastructure', desc: 'Scalable ML pipelines on AWS' }
    ]
  },
  {
    id: 7,
    type: 'market',
    title: 'Market Opportunity',
    subtitle: 'Massive & Growing TAM',
    stats: [
      { value: '$15.4B', label: 'Network Automation Market by 2028', growth: '25.4% CAGR' },
      { value: '$38.2B', label: 'AI in IT Operations (AIOps) by 2030', growth: '32.1% CAGR' },
      { value: '500K+', label: 'Enterprise Networks Globally', growth: 'Underserved' }
    ],
    targetSegments: [
      'Large Enterprises (5000+ employees)',
      'Managed Service Providers (MSPs)',
      'Telecommunications Carriers',
      'Cloud & Data Center Operators'
    ]
  },
  {
    id: 8,
    type: 'traction',
    title: 'Traction & Validation',
    subtitle: 'Proven Results',
    metrics: [
      { value: '70%', label: 'Reduction in Downtime' },
      { value: '24/7', label: 'Expert Availability' },
      { value: '5 min', label: 'Average Resolution Time' },
      { value: '100%', label: 'Compliance Ready' }
    ],
    highlights: [
      { icon: 'Video', title: 'Cisco DevNet Live Demo', desc: 'Netmind App with 5 AI agents powered by fine-tuned LLMs & LRMs', link: 'podcastcisco.vexpertai.com' },
      { icon: 'Users', title: '500+ Engineers', desc: 'Registered for upcoming masterclass' },
      { icon: 'Building', title: 'Enterprise Pilots', desc: 'Active discussions with Fortune 500 companies' }
    ]
  },
  {
    id: 9,
    type: 'team',
    title: 'Our Team',
    subtitle: 'Human Expertise Meets AI Intelligence',
    members: [
      { name: 'Eduard Dulharu', role: 'CTO / Founder', credentials: 'MSc Info Security, Network Architecture Expert', avatar: 'ED' },
      { name: 'Laura Dulharu', role: 'CFO', credentials: 'Financial strategy and operations', avatar: 'LD' },
      { name: 'Rolando Schirmer', role: 'Business Development', credentials: 'Strategic partnerships and growth', avatar: 'RS' },
      { name: 'Tony Kuzman', role: 'Network Architect', credentials: 'CCIE certified infrastructure specialist', avatar: 'TK' },
      { name: 'Sean Daily', role: 'Fractional CMO', credentials: 'LeadEagle, USA - Marketing & Growth', avatar: 'SD' }
    ],
    aiTeam: ['Council of LLMs (AI CEO)', 'AI Marketing Team', 'AI Legal Advisors', 'AI Support Agents']
  },
  {
    id: 10,
    type: 'aws',
    title: 'AWS Infrastructure Needs',
    subtitle: 'Powering Our AI Platform',
    services: [
      { service: 'Amazon SageMaker', usage: 'LLM fine-tuning & model training', priority: 'Critical' },
      { service: 'Amazon Bedrock', usage: 'Foundation model access & RAG', priority: 'Critical' },
      { service: 'Amazon EC2 (GPU)', usage: 'Inference & training workloads', priority: 'High' },
      { service: 'Amazon S3', usage: 'Training data & model storage', priority: 'High' },
      { service: 'Amazon EKS', usage: 'Multi-agent orchestration', priority: 'Medium' },
      { service: 'Amazon CloudWatch', usage: 'Monitoring & observability', priority: 'Medium' }
    ],
    creditsUsage: {
      training: '40%',
      inference: '30%',
      storage: '15%',
      other: '15%'
    }
  },
  {
    id: 11,
    type: 'roadmap',
    title: '2026 Roadmap',
    subtitle: 'Accelerating Growth with AWS',
    quarters: [
      { q: 'Q1 2026', items: ['Migrate Netmind App from Azure to AWS', 'Deploy Next Gen NOC on AWS', 'Deploy Next Gen SOC on AWS'] },
      { q: 'Q2 2026', items: ['Build LLM from scratch for IT Net & Sec', 'Build LRM from scratch for IT Net & Sec', 'Fine-tune models for enterprise use'] },
      { q: 'Q3 2026', items: ['Launch proprietary LRM: vExA', 'vExA beta testing with partners', 'Enterprise pilot expansion'] },
      { q: 'Q4 2026', items: ['vExA production release', '50+ enterprise customers', 'International expansion'] }
    ]
  },
  {
    id: 12,
    type: 'ask',
    title: 'The Ask',
    subtitle: 'AWS Credits to Accelerate Our Mission',
    request: '$100,000',
    requestLabel: 'in AWS Credits',
    impact: [
      { metric: '10x', desc: 'Faster model training cycles' },
      { metric: '50+', desc: 'Enterprise pilots enabled' },
      { metric: '6 mo', desc: 'Accelerated time-to-market' }
    ],
    closing: 'Partner with us to revolutionize network operations with AI'
  }
];

export const companyInfo = {
  name: 'vExpertAI GmbH',
  tagline: 'AI-Powered Network Operations & Security',
  website: 'www.vexpertai.com',
  location: 'Germany',
  founded: '2024'
};
